<template>
  <AppDialog
    class="batch-edit-dialog"
    :title="title"
    :subtitle="subtitle"
    :loading="loading"
    show
    append-to-body
    @close="onClose"
  >
    <SharedEditFields
      :billable.sync="payload.billable"
      :monitor-user-activity.sync="disableUserActivity"
      :disable-project-tracking.sync="disableProjectTrackingValue"
      :activity-tracking-allowed="activityTrackingAllowed"
      can-manage-details
    />
    <div
      v-if="editWarnings.length"
      class="flex-column"
      data-test="edit-warnings"
    >
      <div
        v-for="{ count, label, names } in editWarnings"
        :key="label"
        class="dialog-warning orange mb-5"
      >
        <a
          v-tooltip="{ html: true }"
          :data-original-title="truncatedItems(names, 7).join('<br />')"
          data-tipclass="tooltip-text-left"
        >
          <span>
            <i class="hi hi-warning text-orange mr-5" />
            {{ count }} {{ count | pluralize('project') }}
          </span>
          &nbsp;
        </a>
        {{ label }}
      </div>
    </div>

    <div slot="footer">
      <div class="justify-content-end">
        <AppButton
          type="default"
          @click="onClose"
        >
          Cancel
        </AppButton>
        <AppButton
          type="primary"
          @click="saveChanges()"
        >
          Save
        </AppButton>
      </div>
    </div>
  </AppDialog>
</template>
<script>
import { pluralize } from '@/utils/string_utils';
import { ProjectsResource } from '@/resources';
import SharedEditFields from './SharedEditFields.vue';
import { omit } from 'lodash-es';

export default {
  name: 'ProjectsBatchDialog',
  components: { SharedEditFields },
  props: {
    activityTrackingAllowed: {
      type: Boolean,
      default: true,
    },
    projects: {
      type: Array, // [{ :id, :name, :billable, :monitor_user_activity }]
      required: true,
    },
    organizationId: {
      type: Number,
      required: true,
    },
    organization: {
      type: Object,
      required: true,
    },
    onClose: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      payload: {
        billable: true,
        monitor_user_activity: true,
        disable_project_tracking: this.organization?.settings?.disable_project_tracking ?? false,
      },
      loading: false,
    };
  },
  computed: {
    disableUserActivity: {
      get() {
        // similar as ProjectDialogTabGeneral#disableUserActivity.get
        return !this.payload.monitor_user_activity;
      },
      set(value) {
        this.payload.monitor_user_activity = !value;
      },
    },
    disableProjectTrackingValue: {
      get() {
        return this.payload.disable_project_tracking;
      },
      set(value) {
        this.payload.disable_project_tracking = value;
      },
    },
    editWarnings() {
      const labels = {
        true: { 
          monitor_user_activity: 'will enable activity', 
          billable: 'will become billable',
          disable_project_tracking: 'will require task selection for tracking'
        },
        false: { 
          monitor_user_activity: 'will disable activity', 
          billable: 'will become non-billable',
          disable_project_tracking: 'will allow project tracking'
        },
      };

      return Object.keys(this.filteredPayload)
        .map((key) => {
          const projectsToCheck = this.projects;
          const affectedProjects = projectsToCheck.filter((p) => p[key] !== this.payload[key]);
          const flag = this.payload[key];

          return {
            label: labels[flag][key],
            count: affectedProjects.length,
            names: affectedProjects.map((p) => p.name),
          };
        })
        .filter((item) => item.count > 0);
    },
    title() {
      return `Edit project (${this.projects.length} ${this.projectsLabel})`;
    },
    subtitle() {
      return `Editing the selected ${this.projectsLabel} will override the existing settings.`;
    },
    projectsLabel() {
      return pluralize(this.projects.length, 'project', 'projects');
    },
    filteredPayload() {
      let filtered = { ...this.payload };
      
      if (!this.activityTrackingAllowed) {
        filtered = omit(filtered, 'monitor_user_activity');
      }

      return filtered;
    },
  },
  methods: {
    saveChanges() {
      const payload = {
        project_ids: this.projects.map((p) => p.id),
        changed_attrs: this.filteredPayload,
      };

      this.loading = true;
      ProjectsResource.batchUpdate({ id: this.organizationId }, payload)
        .then(() => window.location.reload())
        .catch((error) => {
          this.loading = false;
          this.$appMessage(error.responseJSON?.alert || error.statusText);
        });
    },
    truncatedItems(items, limit) {
      if (items.length <= limit) {
        return items;
      }

      return items.slice(0, limit).concat(['...']);
    },
  },
};
</script>
